import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ChevronLeft, ChevronRight, ExternalLink, MoveRight, Plus, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import AppointmentSheet from "@/components/AppointmentSheet";
import DayView from "@/components/calendar/DayView";
import WeekView from "@/components/calendar/WeekView";
import MonthView from "@/components/calendar/MonthView";

const VIEWS = [
  { id: "day", label: "Tag" },
  { id: "week", label: "Woche" },
  { id: "multiweek", label: "4 Wochen", mobileLabel: "4W" },
  { id: "month", label: "Monat" },
];

function startOfWeek(d) {
  const x = new Date(d);
  const day = (x.getDay() + 6) % 7; // Monday=0
  x.setHours(0, 0, 0, 0);
  x.setDate(x.getDate() - day);
  return x;
}

export default function CalendarPage() {
  const [view, setView] = useState("multiweek");
  const [cursor, setCursor] = useState(new Date());
  const [sheetOpen, setSheetOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [prefill, setPrefill] = useState(null);
  const [sourceBegutachtungId, setSourceBegutachtungId] = useState(null);
  const [actionAppointment, setActionAppointment] = useState(null);
  const [movingAppointment, setMovingAppointment] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const qc = useQueryClient();

  useEffect(() => {
    const cancelWithEscape = (event) => {
      if (event.key !== "Escape") return;
      setActionAppointment(null);
      setMovingAppointment(null);
    };
    window.addEventListener("keydown", cancelWithEscape);
    return () => window.removeEventListener("keydown", cancelWithEscape);
  }, []);

  // Range for query
  const range = useMemo(() => {
    if (view === "day") {
      const s = new Date(cursor); s.setHours(0, 0, 0, 0);
      const e = new Date(s); e.setDate(e.getDate() + 1);
      return { from: s, to: e };
    }
    if (view === "week") {
      const s = startOfWeek(cursor);
      const e = new Date(s); e.setDate(e.getDate() + 7);
      return { from: s, to: e };
    }
    if (view === "multiweek") {
      const s = startOfWeek(cursor);
      const e = new Date(s); e.setDate(e.getDate() + 28);
      return { from: s, to: e };
    }
    const s = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
    const e = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1);
    // Extend to fully cover the month grid (6 weeks)
    const gridStart = startOfWeek(s);
    const gridEnd = new Date(gridStart); gridEnd.setDate(gridEnd.getDate() + 42);
    return { from: gridStart, to: gridEnd };
  }, [view, cursor]);

  const { data: appointments = [] } = useQuery({
    queryKey: ["appointments", range.from.toISOString(), range.to.toISOString()],
    queryFn: async () => (await api.get("/appointments", { params: {
      from: range.from.toISOString(), to: range.to.toISOString(),
    }})).data,
  });

  const { data: services = [] } = useQuery({
    queryKey: ["services"],
    queryFn: async () => (await api.get("/services")).data,
  });

  // Open existing appointment from ?open=<id>
  useEffect(() => {
    const id = searchParams.get("open");
    if (!id) return undefined;
    let cancelled = false;

    const openAppointmentById = async () => {
      try {
        const a = (await api.get(`/appointments/${id}`)).data;
        if (cancelled) return;
        setCursor(new Date(a.start));
        setEditing(a);
        setPrefill(null);
        setSheetOpen(true);
      } catch (error) {
        if (!cancelled) toast.error(apiError(error));
      } finally {
        if (!cancelled) {
          const next = new URLSearchParams(searchParams);
          next.delete("open");
          setSearchParams(next, { replace: true });
        }
      }
    };

    openAppointmentById();
    return () => { cancelled = true; };
  }, [searchParams, setSearchParams]);

  // New appointment from ?new=1&name=…&email=…  (or ?from_begutachtung=<id>)
  useEffect(() => {
    if (searchParams.get("new") !== "1") return;
    const fromBid = searchParams.get("from_begutachtung");

    // Snapshot params before clearing so async work always sees the intended values.
    const snapshot = new URLSearchParams(searchParams);
    const clearParams = () => {
      const next = new URLSearchParams(searchParams);
      ["new","terminart","name","email","phone","address","customer_id","vehicle_id","car_model","vehicle_brand","vehicle_model","vehicle_year","vehicle_color","color","plate","service_name","notes","from_begutachtung"].forEach(k => next.delete(k));
      setSearchParams(next, { replace: true });
    };

    // Sprint 22 — prefill from a Begutachtung by fetching its full record.
    const buildPrefillFromBegutachtung = async (bid) => {
      try {
        const src = (await api.get(`/appointments/${bid}`)).data;
        const start = new Date(); start.setMinutes(0, 0, 0); start.setHours(start.getHours() + 1);
        const end = new Date(start.getTime() + 60 * 60000);
        setEditing(null);
        setSourceBegutachtungId(bid);
        setPrefill({
          terminart: "arbeitstermin",
          start: start.toISOString(),
          end: end.toISOString(),
          customer_id: src.customer_id || "",
          customer_name: src.customer_name || "",
          phone: src.phone || "",
          email: src.email || "",
          address: src.address || "",
          car_model: src.car_model || "",
          vehicle_brand: src.vehicle_brand || "",
          vehicle_model: src.vehicle_model || "",
          vehicle_year: src.vehicle_year || "",
          vehicle_color: src.vehicle_color || "",
          plate: src.plate || "",
          service_id: src.service_id || "",
          service_name: src.service_name || "",
          color: "#3377FF",
          estimated_price: src.estimated_price || 0,
          notes: src.notes || "",
        });
        setSheetOpen(true);
      } catch (e) {
        toast.error(apiError(e));
      } finally {
        clearParams();
      }
    };

    if (fromBid) {
      buildPrefillFromBegutachtung(fromBid);
    } else {
      const start = new Date(); start.setMinutes(0, 0, 0); start.setHours(start.getHours() + 1);
      const end = new Date(start.getTime() + 60 * 60000);
      setEditing(null);
      setSourceBegutachtungId(null);
      const terminart = snapshot.get("terminart") || "arbeitstermin";
      const serviceName = snapshot.get("service_name") || "";
      setPrefill({
        terminart,
        start: start.toISOString(),
        end: end.toISOString(),
        customer_name: snapshot.get("name") || "",
        email: snapshot.get("email") || "",
        phone: snapshot.get("phone") || "",
        address: snapshot.get("address") || "",
        customer_id: snapshot.get("customer_id") || "",
        vehicle_id: snapshot.get("vehicle_id") || "",
        car_model: snapshot.get("car_model") || "",
        vehicle_brand: snapshot.get("vehicle_brand") || "",
        vehicle_model: snapshot.get("vehicle_model") || "",
        vehicle_year: snapshot.get("vehicle_year") || "",
        vehicle_color: snapshot.get("vehicle_color") || "",
        color: snapshot.get("color") || "#3377FF",
        plate: snapshot.get("plate") || "",
        service_name: serviceName,
        services: terminart === "arbeitstermin" && serviceName
          ? [{ service_id: null, service_name: serviceName, price: 0, duration_min: 0, color: "#3377FF" }]
          : [],
        notes: snapshot.get("notes") || "",
      });
      setSheetOpen(true);
      clearParams();
    }
  }, [searchParams, setSearchParams]);

  const move = (delta) => {
    const d = new Date(cursor);
    if (view === "day") d.setDate(d.getDate() + delta);
    if (view === "week") d.setDate(d.getDate() + delta * 7);
    if (view === "multiweek") d.setDate(d.getDate() + delta * 28);
    if (view === "month") d.setMonth(d.getMonth() + delta);
    setCursor(d);
  };

  const label = useMemo(() => {
    if (view === "day") return cursor.toLocaleDateString("de-DE", { weekday: "long", day: "2-digit", month: "long", year: "numeric" });
    if (view === "week") {
      const s = startOfWeek(cursor); const e = new Date(s); e.setDate(e.getDate() + 6);
      return `${s.toLocaleDateString("de-DE", { day: "2-digit", month: "short" })} – ${e.toLocaleDateString("de-DE", { day: "2-digit", month: "short", year: "numeric" })}`;
    }
    if (view === "multiweek") {
      const s = startOfWeek(cursor); const e = new Date(s); e.setDate(e.getDate() + 27);
      return `${s.toLocaleDateString("de-DE", { day: "2-digit", month: "short" })} – ${e.toLocaleDateString("de-DE", { day: "2-digit", month: "short", year: "numeric" })}`;
    }
    return cursor.toLocaleDateString("de-DE", { month: "long", year: "numeric" });
  }, [view, cursor]);

  const updateMut = useMutation({
    mutationFn: async (appt) => (await api.put(`/appointments/${appt.id}`, appt)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["appointments"] }),
    onError: (e) => toast.error(apiError(e)),
  });

  const handleMoveToDate = (appt, targetDate) => {
    const oldStart = new Date(appt.start);
    const oldEnd = new Date(appt.end);
    const durationMs = oldEnd.getTime() - oldStart.getTime();
    const newStart = new Date(oldStart);
    newStart.setFullYear(targetDate.getFullYear(), targetDate.getMonth(), targetDate.getDate());
    const newEnd = new Date(newStart.getTime() + durationMs);

    if (oldStart.toDateString() === newStart.toDateString()) return false;

    const oldLabel = oldStart.toLocaleDateString("de-DE");
    const newLabel = newStart.toLocaleDateString("de-DE");
    if (!window.confirm(`Termin von ${oldLabel} auf ${newLabel} verschieben?\nUhrzeit und Dauer bleiben unverändert.`)) return false;

    updateMut.mutate(
      { ...appt, start: newStart.toISOString(), end: newEnd.toISOString() },
      { onSuccess: () => toast.success(`Termin auf ${newLabel} verschoben`) },
    );
    return true;
  };

  const handleSlotClick = (start, end) => {
    if (movingAppointment) {
      const durationMs = new Date(movingAppointment.end).getTime() - new Date(movingAppointment.start).getTime();
      const newEnd = new Date(start.getTime() + durationMs);
      const oldLabel = new Date(movingAppointment.start).toLocaleString("de-DE", {
        day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit",
      });
      const newLabel = start.toLocaleString("de-DE", {
        day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit",
      });
      if (!window.confirm(`Termin von ${oldLabel} auf ${newLabel} verschieben?\nDauer bleibt unverändert.`)) return;
      updateMut.mutate(
        { ...movingAppointment, start: start.toISOString(), end: newEnd.toISOString() },
        { onSuccess: () => toast.success(`Termin auf ${newLabel} verschoben`) },
      );
      setMovingAppointment(null);
      return;
    }
    setEditing(null);
    setPrefill({ start: start.toISOString(), end: end.toISOString() });
    setSheetOpen(true);
  };

  const handleApptClick = (a) => {
    setActionAppointment(a);
  };

  const openAppointment = (a) => {
    setActionAppointment(null);
    setMovingAppointment(null);
    setEditing(a);
    setPrefill(null);
    setSheetOpen(true);
  };

  return (
    <div className="calendar-page flex flex-col h-full">
      <div className="stellar-calendar-toolbar flex flex-wrap items-center gap-2 sm:gap-3 px-3 sm:px-5 md:px-7 py-3 sm:py-4">
        <div className="flex items-center gap-1">
          <Button data-testid="cal-prev" variant="outline" size="icon" aria-label="Vorheriger Zeitraum" title="Vorheriger Zeitraum" onClick={() => move(-1)}><ChevronLeft className="h-4 w-4" /></Button>
          <Button data-testid="cal-today" variant="outline" size="sm" onClick={() => setCursor(new Date())}>Heute</Button>
          <Button data-testid="cal-next" variant="outline" size="icon" aria-label="Nächster Zeitraum" title="Nächster Zeitraum" onClick={() => move(1)}><ChevronRight className="h-4 w-4" /></Button>
        </div>
        <div className="font-display text-sm sm:text-lg font-semibold tracking-tight truncate min-w-0">{label}</div>
        <div className="ml-auto flex items-center gap-2 flex-wrap">
          <div className="stellar-segmented">
            {VIEWS.map((v) => (
              <button
                key={v.id}
                data-testid={`view-${v.id}`}
                title={`${v.label}-Ansicht`}
                onClick={() => setView(v.id)}
                className={`px-2.5 sm:px-3.5 py-1.5 text-xs sm:text-sm rounded-lg transition ${view === v.id ? "bg-white text-blue-700 shadow-sm font-semibold" : "text-muted-foreground"}`}
              >
                {v.mobileLabel ? <><span className="hidden sm:inline">{v.label}</span><span className="sm:hidden">{v.mobileLabel}</span></> : v.label}
              </button>
            ))}
          </div>
          <Button className="bg-blue-600 hover:bg-blue-700 rounded-xl shadow-sm" data-testid="new-appointment-btn" size="sm" aria-label="Neuen Termin anlegen" title="Neuen Termin anlegen" onClick={() => {
            // Sprint 11 §10 — inherit the calendar context (selected day / cursor) for new appointments
            const start = new Date(cursor);
            if (view === "month" || view === "multiweek" || view === "day") {
              start.setHours(9, 0, 0, 0);
            } else {
              // Week view: use current hour rounded to full hour + 1
              start.setMinutes(0, 0, 0);
              start.setHours(start.getHours() + 1);
            }
            const end = new Date(start.getTime() + 60 * 60000);
            setEditing(null);
            setPrefill({ start: start.toISOString(), end: end.toISOString() });
            setSheetOpen(true);
          }}>
            <Plus className="h-4 w-4 sm:mr-1" /> <span className="hidden sm:inline">Neuer Termin</span>
          </Button>
        </div>
      </div>

      {movingAppointment && (
        <div className="stellar-month-move-bar mx-3 sm:mx-5 md:mx-7" data-testid="calendar-move-bar">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 font-semibold text-blue-950">
              <MoveRight className="h-4 w-4 shrink-0" />
              Termin verschieben
            </div>
            <div className="truncate text-xs text-blue-800">
              {movingAppointment.car_model || movingAppointment.service_name || movingAppointment.customer_name || "Termin"}
              {" · "}
              {view === "month" || view === "multiweek" ? "Neuen Tag wählen" : "Neuen Tag und neue Uhrzeit wählen"}
            </div>
          </div>
          <button type="button" className="stellar-month-move-action" onClick={() => openAppointment(movingAppointment)}>
            <ExternalLink className="h-4 w-4" /><span>Termin öffnen</span>
          </button>
          <button type="button" className="stellar-month-move-action" onClick={() => setMovingAppointment(null)}>
            <X className="h-4 w-4" /><span>Abbrechen</span>
          </button>
        </div>
      )}

      <div className="stellar-calendar-canvas flex-1 overflow-auto">
        {view === "day" && <DayView date={cursor} appointments={appointments} onApptClick={handleApptClick} onSlotClick={handleSlotClick} movingAppointment={movingAppointment} />}
        {view === "week" && <WeekView date={cursor} appointments={appointments} onApptClick={handleApptClick} onSlotClick={handleSlotClick} movingAppointment={movingAppointment} />}
        {view === "multiweek" && <MonthView multiWeek date={cursor} appointments={appointments} onApptClick={handleApptClick} onDayClick={(d) => { setCursor(d); setView("day"); }} onMoveDate={(a, d) => {
          const accepted = handleMoveToDate(a, d);
          if (accepted !== false) setMovingAppointment(null);
          return accepted;
        }} movingAppointment={movingAppointment} onCancelMove={() => setMovingAppointment(null)} />}
        {view === "month" && <MonthView date={cursor} appointments={appointments} onApptClick={handleApptClick} onDayClick={(d) => { setCursor(d); setView("day"); }} onMoveDate={(a, d) => {
          const accepted = handleMoveToDate(a, d);
          if (accepted !== false) setMovingAppointment(null);
          return accepted;
        }} movingAppointment={movingAppointment} onCancelMove={() => setMovingAppointment(null)} />}
      </div>

      {actionAppointment && (
        <div
          className="fixed inset-0 z-[80] flex items-end justify-center bg-slate-950/25 p-3 sm:items-center"
          onClick={() => setActionAppointment(null)}
          data-testid="calendar-appointment-actions"
        >
          <div
            className="w-full max-w-sm rounded-2xl border border-border bg-card p-4 shadow-2xl"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="mb-4 flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Termin</div>
                <div className="truncate font-display text-lg font-semibold">
                  {actionAppointment.car_model || actionAppointment.service_name || actionAppointment.customer_name || "Termin"}
                </div>
                <div className="mt-1 text-sm text-muted-foreground">
                  {new Date(actionAppointment.start).toLocaleString("de-DE", {
                    weekday: "short", day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit",
                  })}
                </div>
              </div>
              <button type="button" className="rounded-lg p-2 hover:bg-muted" onClick={() => setActionAppointment(null)} aria-label="Schließen">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="grid gap-2">
              <Button className="h-12 justify-start gap-3 rounded-xl" onClick={() => openAppointment(actionAppointment)}>
                <ExternalLink className="h-5 w-5" /> Termin öffnen
              </Button>
              <Button
                variant="outline"
                className="h-12 justify-start gap-3 rounded-xl"
                disabled={["fertig", "archiviert"].includes(actionAppointment.job_status)}
                onClick={() => {
                  setMovingAppointment(actionAppointment);
                  setActionAppointment(null);
                }}
              >
                <MoveRight className="h-5 w-5" />
                {["fertig", "archiviert"].includes(actionAppointment.job_status) ? "Abgeschlossener Auftrag" : "Termin verschieben"}
              </Button>
              <Button variant="ghost" className="h-11 rounded-xl" onClick={() => setActionAppointment(null)}>Abbrechen</Button>
            </div>
          </div>
        </div>
      )}

      <AppointmentSheet
        open={sheetOpen}
        onOpenChange={(v) => { setSheetOpen(v); if (!v) setSourceBegutachtungId(null); }}
        appointment={editing}
        prefill={prefill}
        services={services}
        sourceBegutachtungId={sourceBegutachtungId}
        onSaved={() => qc.invalidateQueries({ queryKey: ["appointments"] })}
      />
    </div>
  );
}
